export const GENERATE_AFTER_OPTIONS = [
  { value: 1, label: "1 Month" },
  { value: 2, label: "2 Months" },
  { value: 3, label: "3 Months" },
];

export const REFERENCE_DATE_OPTIONS = [
  { value: "date_hired", label: "Date Hired" },
  { value: "1st_of_each_month", label: "1st of Each Month" },
];

export const PAF_FREQUENCY_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

export const PAF_GENERATION_SETTINGS_PATHS = Object.freeze({
  list: "appraisals/paf-generation-settings",
  bulk: "appraisals/paf-generation-settings/bulk",
  byId: (id) => `appraisals/paf-generation-settings/${id}`,
});

/**
 * Build a generate-after select option, including values outside the static list.
 */
export const toGenerateAfterOption = (months) => {
  const value = Number(months);
  const existing = GENERATE_AFTER_OPTIONS.find((opt) => opt.value === value);
  if (existing) return existing;
  if (!Number.isFinite(value) || value < 1) return null;
  return {
    value,
    label: value === 1 ? "1 Month" : `${value} Months`,
  };
};

/**
 * Build a reference-date select option from an API value.
 */
export const toReferenceDateOption = (referenceDate) => {
  const existing = REFERENCE_DATE_OPTIONS.find(
    (opt) => opt.value === referenceDate
  );
  if (existing) return existing;
  if (!referenceDate) return REFERENCE_DATE_OPTIONS[0];
  return { value: referenceDate, label: referenceDate };
};

/**
 * Convert frequencies array from API into the UI toggle map.
 */
export const toFrequencyMap = (frequencies = []) => {
  const enabled = new Set(
    (Array.isArray(frequencies) ? frequencies : []).map((item) =>
      String(item).toLowerCase()
    )
  );

  return PAF_FREQUENCY_OPTIONS.reduce((acc, option) => {
    acc[option.value] = enabled.has(option.value);
    return acc;
  }, {});
};

/**
 * Convert UI frequency map into API frequencies array.
 */
export const toFrequencyList = (pafFrequencies = {}) =>
  PAF_FREQUENCY_OPTIONS.filter((option) =>
    Boolean(pafFrequencies?.[option.value])
  ).map((option) => option.value);

/**
 * Map a single API setting into the table row shape used by the UI.
 */
export const mapApiSettingToRow = (setting) => ({
  id: setting.id,
  pafType: setting.paf_type,
  pafTypeLabel: setting.label || setting.paf_type,
  generateAfter: toGenerateAfterOption(setting.generate_after),
  referenceDate: toReferenceDateOption(setting.reference_date),
  pafFrequencies: toFrequencyMap(setting.frequencies),
  autoGenerate: Boolean(setting.auto_generate),
  sortOrder: setting.sort_order ?? 0,
});

/**
 * Map UI rows into the bulk-update API payload.
 */
export const mapRowsToBulkPayload = (rows) => ({
  settings: rows.map((row) => ({
    id: row.id,
    generate_after: Number(row.generateAfter?.value),
    reference_date: row.referenceDate?.value,
    frequencies: toFrequencyList(row.pafFrequencies),
    auto_generate: Boolean(row.autoGenerate),
  })),
});

/**
 * Normalize list payloads from Laravel API Resources or bare arrays.
 */
export const normalizeSettingsList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
};
